import { useState, useRef } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { LoadingButton } from "@atlaskit/button";
import { FormFooter } from "@atlaskit/form";
import Heading from "@atlaskit/heading";
import { Flex, Inline, Stack, xcss } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import {
    SubmitButton,
    SubmitButtonRef
} from "@atlassian/invite-people/dist/types/types";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";
import { PercievedProvisioningTiming } from "@/components/signup/provisioning-funnel-ufo/provisioning-funnel-ufo";
import { ufoEventOnboardingInviteUsers } from "@/components/signup/utils/ufo-events";
import { UfoCustomTimingKey } from "@/components/signup/utils/ufo.types";
import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { ANALYTICS_PREFIX } from "../../../../common/constants";
import { PageInviteLeftProps } from "../../../../common/types";
import { useWizardConfiguration } from "../../../../controllers";
import { onInviteUsersRedirectFailureInvite } from "../../../../utils/redirect-logging";
import { getEmailListAnalytics } from "../utils";

import { InvitePeopleWrapper } from "./invite-people-wrapper";
import messages from "./messages";
import { ShareLink } from "./share-link";

const titleCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h1: {
        fontFamily: wacToken("font.charlie.display"),
        fontWeight: 700,
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const subtextStyles = css({
    font: token("font.body"),
    color: token("color.text.subtle"),
    margin: 0
});

const submitButtonCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        span: {
            fontFamily: wacToken("font.charlie.text") + " !important",
            fontSize: "14px !important",
            lineHeight: "1.5rem !important",
            fontWeight: `${token("font.weight.medium")} !important`
        }
    }
});

const doThisLaterCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        span: {
            fontFamily: wacToken("font.charlie.text") + " !important",
            fontSize: "14px !important",
            lineHeight: "1.5rem !important",
            fontWeight: `${token("font.weight.medium")} !important`,
            // eslint-disable-next-line local/ensure-wac-token-usage
            color: "#505258 !important"
        }
    }
});

const FAILSAFE_REDIRECT_TIMEOUT = 20000;

export const LeftHandSidePageInvite = ({
    onSubmit,
    onClickNext,
    Header
}: PageInviteLeftProps) => {
    const { formatMessage } = useIntl();
    const [isInvitingUsers, setIsInvitingUsers] = useState(false);
    const [isSubmittingInvite, setIsSubmittingInvite] = useState(false);
    const [isSubmittingSkip, setIsSubmittingSkip] = useState(false);

    const [emailList, setEmailList] = useState<string[]>([]);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        teamType,
        projectDetails: { template }
    } = useWizardConfiguration();
    const { signupId } = useSearchParameters();

    const submitRef = useRef<HTMLButtonElement & SubmitButton>(null);

    const renderHeading = () => {
        const titleMsg = isInvitingUsers
            ? messages.titleSubmitting
            : messages.title;
        const bodyMsg = isInvitingUsers
            ? messages.bodyLineOneSubmitting
            : messages.bodyLineOne;
        return (
            <Stack space="space.150">
                <div css={titleCss}>
                    <Heading as="h1" size="xlarge">
                        <FormattedMessage {...titleMsg} />
                    </Heading>
                </div>
                <p css={subtextStyles}>
                    <FormattedMessage {...bodyMsg} />
                </p>
            </Stack>
        );
    };

    const onInviteStart = () => {
        setIsInvitingUsers(true);
    };

    const onClickNextSubmit = async () => {
        setIsSubmittingInvite(true);
        ufoEventOnboardingInviteUsers.setCustomData({ signupId });

        const { emailCount, isCrossDomain } = getEmailListAnalytics(emailList);

        await Promise.resolve(
            createAnalyticsEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: `${ANALYTICS_PREFIX}InviteSubmitButton`,
                eventType: "ui",
                attributes: {
                    emailCount,
                    isCrossDomain,
                    teamType,
                    template
                }
            }).fire()
        );

        if (emailList.length === 0) {
            if (onClickNext) {
                onClickNext();
            }

            return;
        }

        setTimeout(() => {
            onInviteUsersRedirectFailureInvite("time-out");
            if (onClickNext) {
                onClickNext();
            }
        }, FAILSAFE_REDIRECT_TIMEOUT);

        //@ts-ignore
        await submitRef?.current?.onClick();
    };

    const onClickNextDoThisLater = () => {
        setIsSubmittingSkip(true);

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}InviteSkipButton`,
            eventType: "ui"
        }).fire();

        if (onClickNext) {
            onClickNext();
        }
    };

    return (
        <>
            <Flex
                direction="column"
                justifyContent="space-between"
                xcss={containerStyles}
            >
                {checkFeatureFlagGate(
                    featureFlags.provisioningFunnelUfoImprovements
                ) &&
                    isInvitingUsers && (
                        <PercievedProvisioningTiming
                            provisioningFunnelTimingKey={
                                UfoCustomTimingKey.PERCEIVED_PROVISIONING_FUNNEL_INVITE_TEAMMATES
                            }
                        />
                    )}
                <Stack
                    space={
                        checkFeatureFlagGate(
                            featureFlags.jiraOnboardingAiMadlibV2
                        ) && Header
                            ? "space.0"
                            : "space.400"
                    }
                >
                    {checkFeatureFlagGate(
                        featureFlags.jiraOnboardingAiMadlibV2
                    ) && Header ? (
                        <Header
                            header={
                                isInvitingUsers
                                    ? formatMessage(messages.titleSubmitting)
                                    : formatMessage(messages.title)
                            }
                            subheader={
                                isInvitingUsers
                                    ? formatMessage(
                                          messages.bodyLineOneSubmitting
                                      )
                                    : formatMessage(messages.bodyLineOne)
                            }
                        />
                    ) : (
                        renderHeading()
                    )}
                    <Flex direction="column" alignItems="start">
                        <ShareLink />
                        <InvitePeopleWrapper
                            submitRef={submitRef as SubmitButtonRef}
                            onClickNext={onClickNext}
                            onInviteStart={onInviteStart}
                            setEmailList={setEmailList}
                            showEmailInput={!!onSubmit}
                        />
                    </Flex>
                </Stack>
            </Flex>
            <FormFooter>
                <Flex
                    justifyContent="end"
                    alignItems="center"
                    xcss={footerStyles}
                >
                    <Inline
                        alignBlock="center"
                        alignInline="end"
                        space="space.050"
                    >
                        <div css={doThisLaterCss}>
                            <LoadingButton
                                appearance="subtle"
                                onClick={() => {
                                    onClickNextDoThisLater();

                                    if (onSubmit) {
                                        onSubmit();
                                    }
                                }}
                                isDisabled={
                                    emailList.length > 0 ||
                                    isSubmittingInvite ||
                                    isInvitingUsers
                                }
                                isLoading={isSubmittingSkip}
                            >
                                <FormattedMessage {...messages.doThisLater} />
                            </LoadingButton>
                        </div>
                        <div css={submitButtonCss}>
                            <LoadingButton
                                appearance="primary"
                                onClick={() => {
                                    if (onSubmit) {
                                        onSubmit(emailList);
                                    } else {
                                        onClickNextSubmit();
                                    }
                                }}
                                isDisabled={isSubmittingSkip}
                                isLoading={
                                    isInvitingUsers || isSubmittingInvite
                                }
                                ref={submitRef}
                            >
                                <FormattedMessage
                                    {...(emailList.length > 0
                                        ? messages.inviteAndGoToJira
                                        : messages.goToJira)}
                                />
                            </LoadingButton>
                        </div>
                    </Inline>
                </Flex>
            </FormFooter>
        </>
    );
};

const containerStyles = xcss({
    height: "100%"
});

const footerStyles = xcss({
    width: "100%",
    color: "color.text.subtlest"
});
